import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Tooltip } from '@carbon/react';
import {
  Dashboard,
  Temperature,
  Humidity,
  Notification,
  Add,
  ChartLine,
  Settings,
  ArrowRight,
  CheckmarkFilled,
  WarningAltFilled,
  Information,
  CloudOffline,
  Time,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useLiveSource } from '../hooks/useLiveSource';
import { useZones } from '../hooks/useZones';
import { useGatewaysOptimized } from '../hooks/useGatewaysOptimized';
import { useAlerts } from '../hooks/useAlerts';
import GatewayTypeTags from '../components/GatewayTypeTags';
import { formatRelativeTime, formatTemperature, formatHumidity } from '../lib/timeUtils';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  // Fetch data from Supabase (using optimized hooks with Redis cache)
  const { sensors, isLoading: sensorsLoading } = useSensorsOptimized();
  const { zones, isLoading: zonesLoading } = useZones();
  const { gateways, gatewaysCount, isLoading: gatewaysLoading } = useGatewaysOptimized();
  const { alerts, alertsTodayCount, isLoading: alertsLoading } = useAlerts(5);

  // Get sensor IDs for live source tracking
  const sensorIds = useMemo(() => {
    return sensors.map(s => s.id);
  }, [sensors]);

  // Track live data source (gateway vs BLE) for real-time icon updates
  const liveSources = useLiveSource(sensorIds);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Get user display name or email
  const getUserDisplayName = () => {
    if (!user) return '';
    
    // Try to get name from user_metadata (OAuth providers often set this)
    const metadata = user.user_metadata || {};
    const fullName = metadata.full_name || metadata.name;
    const firstName = metadata.first_name;
    const lastName = metadata.last_name;
    
    if (fullName) return fullName;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    
    // Fallback to email (use first part before @)
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    return 'User';
  };

  const userName = getUserDisplayName();

  // Check if any data is still loading
  const isLoading = sensorsLoading || zonesLoading || gatewaysLoading || alertsLoading;

  // Helper function to format last seen time (matching GatewayListPage format)
  const formatLastSeen = (lastSeenAt) => {
    if (!lastSeenAt) return 'Never';
    const date = new Date(lastSeenAt);
    if (!date || isNaN(date.getTime())) return 'Never';
    
    // Format to match gateway detail page: DD MMM @ HH:MM AM/PM
    const day = date.getDate();
    const month = date.toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
    const time = date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).toUpperCase();
    return `${day} ${month} @ ${time}`;
  };

  // Helper function to format last reading time with absolute time and relative time
  // Format: "Jan 19, 12:47 PM (34 min ago)" - converts UTC to local browser timezone
  const formatLastReading = (dateString) => {
    if (!dateString) return 'NO DATA';
    
    const date = new Date(dateString);
    if (!date || isNaN(date.getTime())) return 'NO DATA';
    
    // Calculate relative time (minutes/hours/days ago)
    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - date.getTime()); // Prevent negative values
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    let relativeTime;
    if (diffMins < 1) {
      relativeTime = 'Just now';
    } else if (diffMins < 60) {
      relativeTime = `${diffMins} min ago`;
    } else if (diffHours < 24) {
      relativeTime = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      relativeTime = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
    
    // Format absolute time in local timezone: "Jan 18, 12:47 PM"
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    
    // Format time in local timezone
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const time = `${hours}:${minutesStr} ${ampm}`;
    
    return `${month} ${day}, ${time} (${relativeTime})`;
  };

  // Helper function to get gateway status color
  const getGatewayStatusColor = (status) => {
    if (status === 'online') return '#24a148';
    if (status === 'warning') return '#f1c21b';
    return '#da1e28';
  };

  // Helper function to get gateway status icon
  const getGatewayStatusIcon = (status) => {
    if (status === 'online') {
      return <CheckmarkFilled size={16} style={{ color: '#24a148' }} />;
    } else if (status === 'warning') {
      return <WarningAltFilled size={16} style={{ color: '#f1c21b' }} />;
    }
    return <CloudOffline size={16} style={{ color: '#da1e28' }} />;
  };

  // Calculate device status metrics (active, stale, offline)
  const deviceStatusMetrics = useMemo(() => {
    if (sensorsLoading || sensors.length === 0) {
      return { active: 0, stale: 0, offline: 0 };
    }

    const now = new Date();
    let active = 0;
    let stale = 0;
    let offline = 0;

    sensors.forEach(sensor => {
      // Use sensor.latest_reading from sensor_readings table (via Redis cache)
      // This comes from useSensorsOptimized which fetches from sensor_readings table
      const latestReading = sensor.latest_reading;
      
      // Use created_at for accurate timestamp (not recorded_at which has timezone issues)
      // This matches DeviceListPage logic for consistency
      const timestamp = latestReading?.created_at || latestReading?.recorded_at || null;
      
      if (timestamp) {
        const readingDate = new Date(timestamp);
        const diffMs = now - readingDate;
        const diffMins = Math.floor(diffMs / 60000);
        
        // React Native logic: Consider readings older than 15 minutes as stale
        // But use 60 minutes for offline threshold (more lenient for web)
        const isStale = diffMins > 15;
        const isOffline = diffMins > 60;
        
        if (isOffline) {
          offline++;
        } else if (isStale) {
          stale++;
        } else {
          active++;
        }
      } else {
        // No reading data = offline
        offline++;
      }
    });

    return { active, stale, offline };
  }, [sensors, sensorsLoading]);

  // Calculate gateway status metrics (active/stale/offline)
  const gatewayStatusMetrics = useMemo(() => {
    if (gatewaysLoading || !gateways || gateways.length === 0) {
      return { active: 0, stale: 0, offline: 0 };
    }

    const now = new Date();
    let active = 0;
    let stale = 0;
    let offline = 0;

    gateways.forEach(gateway => {
      // Use gateway.status if available (already calculated by useGateways hook)
      // Otherwise calculate from last_seen_at
      if (gateway.status) {
        if (gateway.status === 'online') {
          active++;
        } else if (gateway.status === 'warning') {
          stale++;
        } else {
          offline++;
        }
      } else {
        // Fallback: Calculate from last_seen_at
        const timestamp = gateway.last_seen_at || gateway.last_seen;
        if (timestamp) {
          const lastSeenDate = new Date(timestamp);
          const diffMs = now - lastSeenDate;
          const diffMins = Math.floor(diffMs / 60000);
          
          // Same logic as devices: <15min = active, 15-60min = stale, >60min = offline
          const isStale = diffMins > 15;
          const isOffline = diffMins > 60;
          
          if (isOffline) {
            offline++;
          } else if (isStale) {
            stale++;
          } else {
            active++;
          }
        } else {
          // No timestamp = offline
          offline++;
        }
      }
    });

    return { active, stale, offline };
  }, [gateways, gatewaysLoading]);

  // Calculate zone status metrics (active/stale/offline) based on sensors in each zone
  const zoneStatusMetrics = useMemo(() => {
    if (zonesLoading || !zones || zones.length === 0) {
      return { active: 0, stale: 0, offline: 0, zoneNames: [] };
    }

    const now = new Date();
    let active = 0;
    let stale = 0;
    let offline = 0;
    const zoneNames = zones.map(z => z.name || `Zone ${z.id.slice(0, 8)}`);

    zones.forEach(zone => {
      const sensorIds = zone.sensor_ids || [];
      
      if (sensorIds.length === 0) {
        // Zone with no sensors = offline
        offline++;
        return;
      }

      // Check status of all sensors in this zone
      let hasActive = false;
      let hasStale = false;
      let hasOffline = false;

      sensorIds.forEach(sensorId => {
        // Find the sensor
        const sensor = sensors.find(s => s.id === sensorId);
        if (!sensor) {
          hasOffline = true;
          return;
        }

        // Get latest reading for this sensor from sensor_readings table
        const latestReading = sensor.latest_reading;
        
        // Determine sensor status
        const timestamp = latestReading?.created_at || latestReading?.recorded_at || null;
        
        if (timestamp) {
          const readingDate = new Date(timestamp);
          const diffMs = now - readingDate;
          const diffMins = Math.floor(diffMs / 60000);
          
          const isStale = diffMins > 15;
          const isOffline = diffMins > 60;
          
          if (isOffline) {
            hasOffline = true;
          } else if (isStale) {
            hasStale = true;
          } else {
            hasActive = true;
          }
        } else {
          // No reading data = offline
          hasOffline = true;
        }
      });

      // Zone status: Active if any sensor is active, Stale if any sensor is stale (but not active), Offline otherwise
      if (hasActive) {
        active++;
      } else if (hasStale) {
        stale++;
      } else {
        offline++;
      }
    });

    return { active, stale, offline, zoneNames };
  }, [zones, sensors, zonesLoading]);

  // Prepare stats from real data
  const stats = [
    { 
      label: 'Total Devices', 
      value: sensorsLoading ? '...' : sensors.length.toString(), 
      icon: Temperature, 
      color: '#0f62fe',
      subMetrics: sensorsLoading ? null : deviceStatusMetrics
    },
    { 
      label: 'Active Zones', 
      value: zonesLoading ? '...' : zones.length.toString(), 
      icon: Dashboard, 
      color: '#6929c4',
      subMetrics: zonesLoading ? null : zoneStatusMetrics
    },
    { 
      label: 'Gateways', 
      value: gatewaysLoading ? '...' : gatewaysCount.toString(), 
      icon: Settings, 
      color: '#24a148',
      subMetrics: gatewaysLoading ? null : gatewayStatusMetrics
    },
    { 
      label: 'Alerts Today', 
      value: alertsLoading ? '...' : alertsTodayCount.toString(), 
      icon: Notification, 
      color: '#da1e28' 
    },
  ];

  // Prepare recent gateways (limit to 3 for dashboard)
  const recentGateways = (gateways || [])
    .slice(0, 3)
    .map((gateway) => ({
      ...gateway,
      name: gateway.name || `Gateway ${gateway.mac_address?.slice(-6) || gateway.id.slice(0, 8)}`,
      lastSeen: gateway.last_seen_at ? formatLastSeen(gateway.last_seen_at) : 'Never',
    }));

  // Prepare recent devices from sensors data
  // Use sensor.latest_reading from sensor_readings table (via Redis cache)
  const recentDevices = sensors
    .slice(0, 5)
    .map(sensor => {
      // Use sensor.latest_reading from sensor_readings table
      // This comes from useSensorsOptimized which fetches from sensor_readings table
      const latestReading = sensor.latest_reading;
      
      const temp = latestReading?.temperature !== undefined 
        ? formatTemperature(latestReading.temperature, sensor.temperature_unit || 'celsius')
        : 'N/A';
      const humidity = latestReading?.humidity !== undefined
        ? formatHumidity(latestReading.humidity)
        : 'N/A';
      
      // Determine status based on last reading time from sensor_readings table
      // Use created_at for accurate timestamp (not recorded_at which has timezone issues)
      // This matches DeviceListPage logic for consistency
      let status = 'offline';
      const timestamp = latestReading?.created_at || latestReading?.recorded_at || null;
      if (timestamp) {
        const readingDate = new Date(timestamp);
        const now = new Date();
        const diffMs = now - readingDate;
        const diffMins = Math.floor(diffMs / 60000);

        // React Native logic: Consider readings older than 15 minutes as stale
        // But use 60 minutes for offline threshold (more lenient for web)
        const isStale = diffMins > 15; // Same as React Native isTimestampOld()
        const isOffline = diffMins > 60; // Web-specific offline threshold

        if (isOffline) {
          status = 'offline';
        } else if (isStale) {
          status = 'warning';
        } else {
          status = 'online';
        }
      }

      // Get MAC address from sensor
      const macAddress = sensor.device_id || sensor.mac_address || null;

      // Get source from sensor_readings table (source column: 'mqtt', 'http', 'app', etc.)
      // Prioritize real-time source from live tracking, fall back to reading source from database
      const liveSourceData = liveSources[sensor.id];
      const source = liveSourceData?.source || latestReading?.source || null;
      const isLive = liveSourceData?.isActive || false;

      return {
        id: sensor.id,
        name: sensor.name,
        zone: sensor.zone_name || 'Unassigned',
        macAddress,
        temp,
        humidity,
        status,
        source,
        isLive,
        lastReading: timestamp
          ? formatLastReading(timestamp)
          : 'NO DATA',
        sensor: sensor, // Keep full sensor object for navigation
      };
    });

  const quickActions = [
    { label: 'Add Device', icon: Add, path: '/dashboard/devices/add', color: '#0f62fe' },
    { label: 'Add Gateway', icon: Settings, path: '/dashboard/gateways/add', color: '#6929c4' },
    { label: 'Add Zone', icon: Dashboard, path: '/dashboard/zones/add', color: '#24a148' },
    { label: 'Alert Rules', icon: ChartLine, path: '/dashboard/rules', color: '#ff832b' },
  ];

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }} data-testid="dashboard-page">
      <style>{`
        /* Carbon Design System Responsive Styles */
        /* Following IBM Carbon breakpoints: sm (320-671px), md (672-1055px), lg (1056px+) */
        
        /* Small devices (mobile) - max-width: 671px */
        @media (max-width: 671px) {
          [data-testid="dashboard-page"] {
            padding: 16px 0 !important;
          }

          [data-testid="dashboard-page"] .cds--grid {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          [data-testid="dashboard-page"] h1 {
            font-size: 1.75rem !important;
            line-height: 1.25 !important;
            margin-bottom: 8px !important;
          }

          [data-testid="dashboard-page"] h2 {
            font-size: 1.125rem !important;
            line-height: 1.4 !important;
            margin-bottom: 12px !important;
          }

          /* Stats Cards - Full width stacked on mobile */
          [data-testid^="stat-"] {
            margin-bottom: 16px !important;
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          [data-testid^="stat-"] > div {
            padding: 20px 16px !important;
            min-height: 120px;
          }

          /* Make Grid row use flexbox for equal height columns */
          .bx--grid > .bx--row {
            display: flex;
            flex-wrap: wrap;
            align-items: stretch;
          }

          /* Make sure columns stretch to equal height and cards have consistent minimum height */
          @media (min-width: 672px) {
            [data-testid^="stat-"] {
              min-height: 220px;
            }
          }

          [data-testid^="stat-"] p:first-of-type {
            font-size: 0.875rem !important;
            margin-bottom: 8px !important;
          }

          [data-testid^="stat-"] p:last-of-type {
            font-size: 2rem !important;
            line-height: 1.2 !important;
          }

          /* Quick Actions - 2 columns on mobile */
          [data-testid^="action-"] {
            margin-bottom: 16px !important;
          }

          [data-testid^="action-"] > div {
            padding: 20px 16px !important;
            min-height: auto !important;
            height: auto !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
          }

          [data-testid^="action-"] > div > div {
            width: 56px !important;
            height: 56px !important;
            min-width: 56px !important;
            min-height: 56px !important;
            max-width: 56px !important;
            max-height: 56px !important;
            margin: 0 auto 12px !important;
            flex-shrink: 0 !important;
          }

          [data-testid^="action-"] p {
            font-size: 0.875rem !important;
            line-height: 1.3 !important;
            margin: 0 !important;
            text-align: center !important;
          }

          /* Recent Gateways - Better mobile layout */
          [data-testid^="gateway-"] > div {
            padding: 16px !important;
          }

          [data-testid^="gateway-"] > div > div:first-child {
            flex-direction: column !important;
            gap: 16px !important;
            align-items: flex-start !important;
          }

          [data-testid^="gateway-"] > div > div:first-child > div:first-child {
            width: 100%;
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
          }

          [data-testid^="gateway-"] > div > div:first-child > div:last-child {
            width: 100%;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 12px !important;
          }

          [data-testid^="gateway-"] > div > div:first-child > div:last-child > div {
            min-width: 70px;
          }

          /* Recent Devices - Better mobile layout */
          [data-testid^="device-"] > div {
            padding: 16px !important;
          }

          [data-testid^="device-"] > div > div:first-child {
            flex-direction: column !important;
            gap: 16px !important;
            align-items: flex-start !important;
          }

          [data-testid^="device-"] > div > div:first-child > div:first-child {
            width: 100%;
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
          }

          [data-testid^="device-"] > div > div:first-child > div:last-child {
            width: 100%;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 12px !important;
          }

          [data-testid^="device-"] > div > div:first-child > div:last-child > div {
            min-width: 70px;
          }

          /* View All button on mobile */
          [data-testid="view-all-devices"] {
            font-size: 0.75rem !important;
            padding: 8px 12px !important;
            min-height: 32px !important;
          }

          /* Recent Alerts on mobile */
          [data-testid="dashboard-page"] .cds--tile {
            margin-bottom: 16px !important;
          }
        }

        /* Medium devices (tablet) - 672px to 1055px */
        @media (min-width: 672px) and (max-width: 1055px) {
          [data-testid="dashboard-page"] {
            padding: 32px 0 !important;
          }

          [data-testid="dashboard-page"] .cds--grid {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          /* Stats Cards - 2 columns on tablet */
          [data-testid^="stat-"] {
            margin-bottom: 20px !important;
          }

          [data-testid^="stat-"] > div {
            padding: 20px !important;
          }

          /* Quick Actions - 2 columns on tablet */
          [data-testid^="action-"] {
            margin-bottom: 20px !important;
          }

          [data-testid^="action-"] > div {
            padding: 24px 20px !important;
          }
        }

        /* Extra small devices - max-width: 480px */
        @media (max-width: 480px) {
          [data-testid="dashboard-page"] {
            padding: 12px 0 !important;
          }

          [data-testid="dashboard-page"] .cds--grid {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }

          [data-testid="dashboard-page"] h1 {
            font-size: 1.5rem !important;
            margin-bottom: 4px !important;
          }

          [data-testid="dashboard-page"] h2 {
            font-size: 1rem !important;
            margin-bottom: 8px !important;
          }

          /* Compact stats on very small screens */
          [data-testid^="stat-"] > div {
            padding: 16px 12px !important;
            min-height: 100px !important;
          }

          [data-testid^="stat-"] p:first-of-type {
            font-size: 0.75rem !important;
          }

          [data-testid^="stat-"] p:last-of-type {
            font-size: 1.75rem !important;
          }

          [data-testid^="stat-"] > div > div:last-child {
            padding: 10px !important;
          }

          /* Compact quick actions */
          [data-testid^="action-"] > div {
            padding: 18px 12px !important;
            min-height: auto !important;
            height: auto !important;
          }

          [data-testid^="action-"] > div > div {
            width: 48px !important;
            height: 48px !important;
            min-width: 48px !important;
            min-height: 48px !important;
            max-width: 48px !important;
            max-height: 48px !important;
            margin: 0 auto 10px !important;
            flex-shrink: 0 !important;
          }

          [data-testid^="action-"] p {
            font-size: 0.8125rem !important;
            line-height: 1.25 !important;
            margin: 0 !important;
          }

          /* Compact gateway cards */
          [data-testid^="gateway-"] > div {
            padding: 12px !important;
          }

          [data-testid^="gateway-"] > div > div:first-child > div:first-child {
            gap: 10px !important;
          }

          [data-testid^="gateway-"] > div > div:first-child > div:last-child {
            gap: 8px !important;
          }

          [data-testid^="gateway-"] > div > div:first-child > div:last-child > div {
            min-width: 60px;
          }

          /* Compact device cards */
          [data-testid^="device-"] > div {
            padding: 12px !important;
          }

          [data-testid^="device-"] > div > div:first-child > div:first-child {
            gap: 10px !important;
          }

          [data-testid^="device-"] > div > div:first-child > div:last-child {
            gap: 8px !important;
          }

          [data-testid^="device-"] > div > div:first-child > div:last-child > div {
            min-width: 60px;
          }

          /* Smaller buttons and text */
          [data-testid="view-all-devices"] {
            font-size: 0.6875rem !important;
            padding: 6px 10px !important;
            min-height: 28px !important;
          }
        }

        /* Hover effects for interactive elements (desktop only) */
        @media (min-width: 1056px) {
          [data-testid^="action-"].feature-card:hover > div {
            transform: translateY(-4px);
            transition: transform 0.2s ease-in-out;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }

          [data-testid^="device-"]:hover > div {
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
            transition: box-shadow 0.2s ease-in-out;
          }
        }
      `}</style>
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, margin: 0 }}>
                Welcome back! {userName}
              </h1>
              <Tooltip
                label="The Dashboard provides a comprehensive overview of your cold chain monitoring system. View real-time sensor data, gateway status, zone health, and recent alerts all in one place."
                align="bottom"
                alignOffset={4}
              >
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    color: textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Dashboard help"
                >
                  <Information size={20} />
                </button>
              </Tooltip>
            </div>
            <p style={{ color: textSecondary, margin: 0 }}>Here's an overview of your monitoring system</p>
          </div>
        </Column>

        {/* Stats Cards */}
        {stats.map((stat, idx) => (
          <Column lg={4} md={4} sm={4} key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '24px',
                marginBottom: '24px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              data-testid={`stat-${idx}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '8px', flexShrink: 0 }}>{stat.label}</p>
                  <p style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: stat.subMetrics ? '12px' : '0', flexShrink: 0 }}>{stat.value}</p>
                  {/* Sub-metrics for Total Devices, Gateways, and Zones */}
                  {stat.subMetrics && (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#24a148', flexShrink: 0 }} />
                          <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                            Active: <strong style={{ color: textPrimary }}>{stat.subMetrics.active}</strong>
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ff832b', flexShrink: 0 }} />
                          <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                            Stale: <strong style={{ color: textPrimary }}>{stat.subMetrics.stale}</strong>
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#8d8d8d', flexShrink: 0 }} />
                          <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                            Offline: <strong style={{ color: textPrimary }}>{stat.subMetrics.offline}</strong>
                          </span>
                        </div>
                      </div>
                      {/* Zone names for Zones card */}
                      {stat.subMetrics.zoneNames && stat.subMetrics.zoneNames.length > 0 && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${borderColor}`, flexShrink: 0 }}>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '6px', fontWeight: 500 }}>Zones:</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {stat.subMetrics.zoneNames.map((name, idx) => (
                              <span
                                key={idx}
                                style={{
                                  color: textPrimary,
                                  fontSize: '0.6875rem',
                                  padding: '4px 8px',
                                  backgroundColor: isDark ? '#393939' : '#e0e0e0',
                                  borderRadius: '4px',
                                  display: 'inline-block'
                                }}
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Spacer for cards without sub-metrics to match height */}
                  {!stat.subMetrics && (
                    <div style={{ flex: 1 }} />
                  )}
                </div>
                <div style={{ backgroundColor: stat.color, borderRadius: '8px', padding: '12px', flexShrink: 0, alignSelf: 'flex-start' }}>
                  <stat.icon size={24} style={{ color: '#ffffff' }} />
                </div>
              </div>
            </Tile>
          </Column>
        ))}

        {/* Quick Actions */}
        <Column lg={16} md={8} sm={4}>
          <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Quick Actions</h2>
        </Column>
        {quickActions.map((action, idx) => (
          <Column lg={4} md={4} sm={2} key={idx}>
            <Tile
              className="feature-card"
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '24px',
                marginBottom: '24px',
                cursor: 'pointer',
                textAlign: 'center',
              }}
              onClick={() => navigate(action.path)}
              data-testid={`action-${idx}`}
            >
              <div style={{ 
                backgroundColor: action.color, 
                borderRadius: '50%', 
                width: '48px', 
                height: '48px',
                minWidth: '48px',
                minHeight: '48px',
                maxWidth: '48px',
                maxHeight: '48px',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 12px',
                flexShrink: 0
              }}>
                <action.icon size={24} style={{ color: '#ffffff' }} />
              </div>
              <p style={{ color: textPrimary, fontWeight: 500 }}>{action.label}</p>
            </Tile>
          </Column>
        ))}

        {/* Recent Gateways */}
        <Column lg={6} md={8} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600 }}>Recent Gateways</h2>
            <Button kind="ghost" size="sm" onClick={() => navigate('/dashboard/gateways')} data-testid="view-all-gateways">
              View All <ArrowRight size={16} style={{ marginLeft: '4px' }} />
            </Button>
          </div>
          {gatewaysLoading ? (
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <p style={{ color: textSecondary }}>Loading gateways...</p>
            </Tile>
          ) : recentGateways.length === 0 ? (
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <p style={{ color: textSecondary }}>No gateways found. Add your first gateway to get started.</p>
              <Button 
                kind="primary" 
                size="sm" 
                onClick={() => navigate('/dashboard/gateways/add')}
                style={{ marginTop: '16px' }}
              >
                Add Gateway
              </Button>
            </Tile>
          ) : (
            recentGateways.map((gateway) => (
              <Tile
                key={gateway.id}
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                  borderLeft: `4px solid ${getGatewayStatusColor(gateway.status)}`,
                  padding: '20px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/dashboard/gateways/${gateway.id}`)}
                data-testid={`gateway-${gateway.id}`}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    flex: '1 1 auto',
                    minWidth: '200px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {getGatewayStatusIcon(gateway.status)}
                    </div>
                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '4px', wordBreak: 'break-word' }}>{gateway.name}</p>
                      <div style={{ marginBottom: '6px' }}>
                        <GatewayTypeTags gateway={gateway} />
                      </div>
                      <div style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {gateway.mac_address && (
                          <span style={{ fontFamily: 'monospace' }}>{gateway.mac_address}</span>
                        )}
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '4px' }}>
                        Last seen: {gateway.lastSeen}
                      </p>
                    </div>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '8px',
                    alignItems: 'flex-end',
                    flexShrink: 0,
                    minWidth: '120px'
                  }}>
                    <div style={{ width: '100%' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Connected Devices</p>
                      {gateway.connectedDevices === 0 ? (
                        <p style={{ color: textSecondary, fontWeight: 600, fontSize: '1rem', margin: 0 }}>0</p>
                      ) : (gateway.offlineDevices === 0 && gateway.warningDevices === 0) ? (
                        <p style={{ color: '#24a148', fontWeight: 600, fontSize: '1rem', margin: 0 }}>
                          {gateway.onlineDevices || gateway.connectedDevices} Online
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {(gateway.onlineDevices || 0) > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <CheckmarkFilled size={14} style={{ color: '#24a148' }} />
                              <span style={{ color: '#24a148', fontWeight: 600, fontSize: '0.875rem' }}>
                                {gateway.onlineDevices} Online
                              </span>
                            </div>
                          )}
                          {(gateway.warningDevices || 0) > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Time size={14} style={{ color: '#f1c21b' }} />
                              <span style={{ color: '#f1c21b', fontWeight: 600, fontSize: '0.875rem' }}>
                                {gateway.warningDevices} Stale
                              </span>
                            </div>
                          )}
                          {(gateway.offlineDevices || 0) > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <CloudOffline size={14} style={{ color: '#da1e28' }} />
                              <span style={{ color: '#da1e28', fontWeight: 600, fontSize: '0.875rem' }}>
                                {gateway.offlineDevices} Offline
                              </span>
                            </div>
                          )}
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0, marginTop: '2px' }}>
                            {gateway.connectedDevices} Total
                          </p>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Tag type={gateway.status === 'online' ? 'green' : gateway.status === 'warning' ? 'red' : 'gray'}>
                        {gateway.status}
                      </Tag>
                    </div>
                  </div>
                </div>
              </Tile>
            ))
          )}

          {/* Recent Alerts - Stacked below Recent Gateways */}
          <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600 }}>Recent Alerts</h2>
            </div>
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '20px',
              }}
            >
              {alertsLoading ? (
                <p style={{ color: textSecondary, textAlign: 'center', padding: '20px' }}>Loading alerts...</p>
              ) : alerts.length === 0 ? (
                <p style={{ color: textSecondary, textAlign: 'center', padding: '20px' }}>No alerts</p>
              ) : (
                alerts.map((alert, idx) => {
                  const alertType = alert.severity || alert.alert_type || 'warning';
                  const alertColor = alertType === 'error' || alertType === 'critical' 
                    ? '#da1e28' 
                    : alertType === 'warning' 
                    ? '#ff832b' 
                    : '#0f62fe';
                  
                  return (
                    <div
                      key={alert.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px 0',
                        borderBottom: idx < alerts.length - 1 ? `1px solid ${borderColor}` : 'none',
                      }}
                    >
                      <Notification
                        size={16}
                        style={{ color: alertColor, marginTop: '2px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <p style={{ color: textPrimary, fontSize: '0.875rem', marginBottom: '4px' }}>
                          {alert.message}
                        </p>
                        <p style={{ color: textSecondary, fontSize: '0.75rem' }}>
                          {formatRelativeTime(alert.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </Tile>
          </div>
        </Column>

        {/* Recent Devices */}
        <Column lg={10} md={8} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600 }}>Recent Devices</h2>
            <Button kind="ghost" size="sm" onClick={() => navigate('/dashboard/devices')} data-testid="view-all-devices">
              View All <ArrowRight size={16} style={{ marginLeft: '4px' }} />
            </Button>
          </div>
          {sensorsLoading ? (
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <p style={{ color: textSecondary }}>Loading devices...</p>
            </Tile>
          ) : recentDevices.length === 0 ? (
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <p style={{ color: textSecondary }}>No devices found. Add your first device to get started.</p>
              <Button 
                kind="primary" 
                size="sm" 
                onClick={() => navigate('/dashboard/devices/add')}
                style={{ marginTop: '16px' }}
              >
                Add Device
              </Button>
            </Tile>
          ) : (
            recentDevices.map((device) => (
              <Tile
                key={device.id}
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                  padding: '20px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/dashboard/devices/${device.id}`)}
                data-testid={`device-${device.id}`}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    flex: '1 1 auto',
                    minWidth: '200px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                    {device.status === 'online' ? (
                      <CheckmarkFilled size={20} style={{ color: '#24a148' }} />
                    ) : (
                      <WarningAltFilled size={20} style={{ color: '#ff832b' }} />
                    )}
                    </div>
                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '4px', wordBreak: 'break-word' }}>{device.name}</p>
                      <div style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        <span>{device.zone} • {device.lastReading}</span>
                        {device.source && (
                          <>
                            <span>{' • '}</span>
                            {/* Show actual source as text tag */}
                            {device.source === 'mqtt' ? (
                              <Tag type="purple" size="sm">MQTT</Tag>
                            ) : device.source === 'http' ? (
                              <Tag type="cyan" size="sm">HTTP</Tag>
                            ) : device.source === 'app' ? (
                              <Tag type="blue" size="sm">APP</Tag>
                            ) : (
                              <Tag type="gray" size="sm">{device.source.toUpperCase()}</Tag>
                            )}
                            {/* Show LIVE indicator if actively receiving data */}
                            {device.isLive && (
                              <>
                                <Tag type="red" size="sm">LIVE</Tag>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      {device.macAddress && (
                        <p style={{ color: textSecondary, fontSize: '0.7rem', fontFamily: 'monospace', marginTop: '2px' }}>
                          {device.macAddress}
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    flexShrink: 0
                  }}>
                    <div style={{ textAlign: 'center', minWidth: '60px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Temp</p>
                      <p style={{ color: '#0f62fe', fontWeight: 600, fontSize: '0.875rem' }}>{device.temp}</p>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: '60px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Humidity</p>
                      <p style={{ color: '#6929c4', fontWeight: 600, fontSize: '0.875rem' }}>{device.humidity}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Tag type={device.status === 'online' ? 'green' : device.status === 'warning' ? 'red' : 'gray'}>
                      {device.status}
                    </Tag>
                      {device.status === 'warning' && (
                        <Tooltip
                          align="bottom"
                          label={
                            <div style={{ maxWidth: '250px', padding: '4px' }}>
                              <strong>Warning Status</strong>
                              <br />
                              <br />
                              No readings received in the last 15-60 minutes. This may be normal if your sync settings are configured for periodic updates.
                              <br />
                              <br />
                              <strong>To refresh data:</strong> Use the mobile app to manually sync the device, or check device range, battery level, or connectivity if sync settings allow more frequent updates.
                            </div>
                          }
                        >
                          <button
                            type="button"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              color: textSecondary,
                            }}
                            aria-label="Warning status information"
                          >
                            <Information size={14} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </Tile>
            ))
          )}
        </Column>
      </Grid>
    </div>
  );
};

export default DashboardPage;
